import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowTrendUp,
  faBuilding,
  faMoneyBillTransfer,
  faPercent,
  faShieldHalved,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";

const brl = (value) =>
  (Number(value || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
const pct = (value) =>
  value === null || value === undefined
    ? "Sem dados"
    : `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
const Stat = ({ icon, label, value, detail, tone }) => (
  <article className="afxp-stat">
    <span className={`afxp-stat__icon ${tone}`}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <div>
      <small>{label}</small>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  </article>
);

export default function AdminFluxopayRealOverviewV2({ setTab }) {
  const [data, setData] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      api.get("/api/admin/fluxopay/real-overview"),
      api.get("/api/admin/fluxopay/accounts", { params: { per_page: 100 } }),
    ])
      .then(([overview, accounts]) => {
        setData(overview.data);
        setClinics(
          [...(accounts.data.data || [])].sort(
            (a, b) =>
              Number(b.gross_volume_cents || 0) -
              Number(a.gross_volume_cents || 0),
          ),
        );
      })
      .catch((err) =>
        setError(
          err.response?.data?.message ||
            "Não foi possível carregar os indicadores reais.",
        ),
      );
  }, []);
  const maxTpv = useMemo(
    () =>
      Math.max(
        1,
        ...(data?.monthly || []).map((item) => Number(item.tpv_cents || 0)),
      ),
    [data],
  );
  if (error)
    return <section className="afxp-card afxp-warning">{error}</section>;
  if (!data)
    return <section className="afxp-card">Carregando indicadores...</section>;
  const card = data.payment_methods?.credit_card?.total_cents || 0;
  const pix = data.payment_methods?.pix?.total_cents || 0;
  const total = Number(card) + Number(pix);
  const cardShare = total > 0 ? (Number(card) / total) * 100 : 0;
  const attention =
    Number(data.reconciliation?.divergent || 0) +
    Number(data.accounts?.blocked || 0);
  return (
    <>
      <section className="afxp-stats">
        <Stat
          icon={faArrowTrendUp}
          label="TPV confirmado"
          value={brl(data.tpv_cents)}
          detail={`${data.transactions.paid || 0} pagamentos aprovados`}
          tone="green"
        />
        <Stat
          icon={faPercent}
          label="Receita Fluxopay"
          value={brl(data.platform_revenue_cents)}
          detail={`Custo Asaas ${brl(data.provider_cost_cents)}`}
          tone="purple"
        />
        <Stat
          icon={faBuilding}
          label="Clínicas ativas"
          value={data.accounts.active || 0}
          detail={`${data.accounts.review || 0} em análise`}
          tone="blue"
        />
        <Stat
          icon={faMoneyBillTransfer}
          label="Saldo sob gestão"
          value={brl(data.managed_balance_cents)}
          detail="Disponível + em processamento no Asaas"
          tone="navy"
        />
        <Stat
          icon={faTriangleExclamation}
          label="Atenção operacional"
          value={attention}
          detail={`${data.reconciliation.divergent || 0} divergência(s) · ${data.accounts.blocked || 0} conta(s) com restrição`}
          tone="red"
        />
      </section>
      <section className="afxp-grid afxp-grid--overview">
        <article className="afxp-card afxp-volume">
          <header>
            <div>
              <small>VOLUME TRANSACIONADO</small>
              <h3>Últimos seis meses</h3>
            </div>
          </header>
          <div className="afxp-chart">
            {(data.monthly || []).map((item) => (
              <div key={item.key}>
                <span
                  style={{
                    height: `${Math.max(2, (Number(item.tpv_cents || 0) / maxTpv) * 100)}%`,
                  }}
                />
                <i
                  style={{
                    height: `${Math.max(2, (Number(item.platform_revenue_cents || 0) / maxTpv) * 100)}%`,
                  }}
                />
                <small>{item.label}</small>
              </div>
            ))}
          </div>
          <footer>
            <span>
              <i className="tpv" />
              TPV <b>{brl(data.tpv_cents)}</b>
            </span>
            <span>
              <i className="revenue" />
              Receita Fluxopay <b>{brl(data.platform_revenue_cents)}</b>
            </span>
          </footer>
        </article>
        <article className="afxp-card afxp-health">
          <header>
            <div>
              <small>SAÚDE DA OPERAÇÃO</small>
              <h3>Indicadores observados</h3>
            </div>
            <FontAwesomeIcon icon={faShieldHalved} />
          </header>
          <div>
            <span>Taxa de aprovação</span>
            <b>{pct(data.transactions.approval_percentage)}</b>
            <em>{data.transactions.paid} aprovadas</em>
          </div>
          <div>
            <span>Chargebacks registrados</span>
            <b>{data.transactions.chargeback || 0}</b>
            <em>Dados internos</em>
          </div>
          <div>
            <span>Estornos registrados</span>
            <b>{data.transactions.refunded || 0}</b>
            <em>Dados internos</em>
          </div>
          <div>
            <span>Execuções conciliadas</span>
            <b>{pct(data.reconciliation.matched_percentage)}</b>
            <em>{data.reconciliation.divergent || 0} divergente(s)</em>
          </div>
          <button onClick={() => setTab("reconciliation")}>
            Ver conciliação <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </article>
      </section>
      <section className="afxp-grid afxp-grid--bottom">
        <article className="afxp-card">
          <header>
            <div>
              <small>CLÍNICAS</small>
              <h3>Maiores volumes confirmados</h3>
            </div>
            <button className="afxp-link" onClick={() => setTab("clinics")}>
              Ver todas
            </button>
          </header>
          {clinics.slice(0, 4).map((account, index) => (
            <div className="afxp-ranking" key={account.id}>
              <span>{index + 1}</span>
              <div>
                <b>
                  {account.entidade?.nome || `Entidade #${account.entidade_id}`}
                </b>
                <small>{account.payment_orders_count || 0} transações</small>
              </div>
              <strong>{brl(account.gross_volume_cents)}</strong>
            </div>
          ))}
        </article>
        <article className="afxp-card">
          <header>
            <div>
              <small>MEIOS DE PAGAMENTO</small>
              <h3>Composição real do TPV</h3>
            </div>
          </header>
          <div
            className="afxp-donut"
            style={{
              background: `conic-gradient(#173f56 0 ${cardShare}%,#17a589 ${cardShare}% 100%)`,
            }}
          >
            <div>
              <span>{pct(cardShare)}</span>
              <small>Cartão</small>
            </div>
          </div>
          <div className="afxp-legend">
            <span>
              <i className="card" />
              Cartão de crédito <b>{brl(card)}</b>
            </span>
            <span>
              <i className="pix" />
              Pix <b>{brl(pix)}</b>
            </span>
          </div>
        </article>
      </section>
    </>
  );
}
